'use client';

import FilterListIcon from '@mui/icons-material/FilterList';
import SettingsIcon from '@mui/icons-material/Settings';
import { Box, Card, CardContent, IconButton } from '@mui/material';

import { ComponentErrorBoundary, PriceChart, TimeRangeChipList } from '@/components';

export interface HistoricalDataPoint {
  date: string;
  values: Record<string, number>;
  timestamp: number;
}

const TIME_RANGES = ['1H', '6H', '1D', '1W', '1M', 'ALL'] as const;

interface ChartMarketConfig {
  id: string;
  name: string;
  color: string;
  marketId: string;
  groupItemTitle?: string;
  question?: string;
}

interface MarketChartSectionProps {
  markets: ChartMarketConfig[];
  historicalData: HistoricalDataPoint[];
  selectedRange: string;
  onRangeChange: (range: string) => void;
  isLoading?: boolean;
}

export function MarketChartSection({
  markets,
  historicalData,
  selectedRange,
  onRangeChange,
  isLoading,
}: MarketChartSectionProps) {
  const createRangeClickHandler = (range: string) => () => {
    onRangeChange(range);
  };

  return (
    <ComponentErrorBoundary componentName="Price Chart">
      <Card sx={{ borderRadius: { xs: 2, sm: 3 }, mb: { xs: 2, md: 2.5 } }}>
        <CardContent
          sx={{
            p: { xs: 1.5, sm: 2, md: 3 },
            '&:last-child': { pb: { xs: 1.5, sm: 2, md: 3 } },
          }}
        >
          <Box sx={{ width: '100%', overflowX: 'auto' }}>
            <PriceChart
              markets={markets}
              data={historicalData}
              selectedRange={selectedRange}
              isLoading={isLoading}
            />
          </Box>

          {/* Time Range Selector - Bottom Right of Chart */}
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'flex-end',
              mt: 2,
            }}
          >
            <Box
              sx={{
                display: 'flex',
                gap: 0.75,
                overflowX: 'auto',
                pb: 0.5,
              }}
            >
              <TimeRangeChipList
                ranges={TIME_RANGES}
                selectedRange={selectedRange}
                onRangeClick={createRangeClickHandler}
              />
            </Box>
          </Box>
        </CardContent>
      </Card>
    </ComponentErrorBoundary>
  );
}
